// Incoming Interac e-Transfers, read from the e-Transfer notification
// emails by the Outlook sync and matched to customers. Unmatched ones
// wait here for a one-click assign.

import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext.jsx';
import EtransferAssign from '../components/EtransferAssign.jsx';

export const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const fmtDate = (ts) => new Date(ts).toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });

const METHOD = {
  learned: 'matched by sender',
  phone: 'matched by phone',
  site: 'matched by site',
  name: 'matched by name',
  manual: 'assigned by hand',
  invoice: 'matched by QuickBooks invoice',
  'sender email': 'matched by sender email',
  vendor: 'vendor'
};

// "Inv 5167 · $421.80 · paid" under the message.
export function InvoiceInfo({ r }) {
  if (!r.invoice_numbers || !r.invoice_numbers.length) return null;
  const label = `Inv ${r.invoice_numbers.join(', ')}`;
  if (!r.qbo_invoice_ids) return <div className="calls-sub">{label} · not checked in QuickBooks yet</div>;
  if (!r.qbo_invoice_ids.length) return <div className="calls-sub et-warn">{label} · not found in QuickBooks</div>;
  const total = Number(r.invoice_total);
  const diff = Math.round((Number(r.amount) - total) * 100) / 100;
  const lines = Array.isArray(r.invoice_lines) ? r.invoice_lines : [];
  const paid = (b) => Number(b) <= 0.005;
  return (
    <div className="et-inv">
      {lines.length > 0 ? lines.map((l) => (
        <div key={l.qbo_id} className="et-inv-line">
          <span className="et-inv-doc">Inv {l.doc}</span>
          <span className="et-inv-amt">{fmtMoney(l.total)}</span>
          <span className="et-inv-site">
            {l.site_id
              ? <>{l.site_name || l.site_number}{l.site_number && l.site_name ? ` (${l.site_number})` : ''}</>
              : l.memo ? <span className="et-warn" title="QuickBooks memo; no matching site in Command">{l.memo}</span>
              : <span className="calls-dim">no site on invoice</span>}
          </span>
          {paid(l.balance) ? <span className="et-ok">paid</span> : <span className="et-warn">{fmtMoney(l.balance)} open</span>}
        </div>
      )) : (
        <div className="calls-sub">
          {label} · {fmtMoney(total)}{r.qbo_customer_name ? ` · ${r.qbo_customer_name}` : ''}
          {' · '}{paid(r.invoice_balance) ? <span className="et-ok">paid in QuickBooks</span> : <span className="et-warn">{fmtMoney(r.invoice_balance)} still open in QuickBooks</span>}
        </div>
      )}
      {Math.abs(diff) >= 0.01 && <div className="calls-sub et-warn">Payment is {diff > 0 ? `${fmtMoney(diff)} over` : `${fmtMoney(-diff)} short`} the invoice total ({fmtMoney(total)})</div>}
    </div>
  );
}

// Every site the payment covers: site number, name and how much of the
// payment went to it (from the invoices). Falls back to the single site.
export function SiteCell({ r }) {
  const lines = Array.isArray(r.invoice_lines) ? r.invoice_lines : [];
  const bySite = new Map();
  for (const l of lines) {
    if (!l.site_id) continue;
    const cur = bySite.get(l.site_id) || { ...l, amount: 0, docs: [] };
    cur.amount += Number(l.total || 0);
    cur.docs.push(l.doc);
    bySite.set(l.site_id, cur);
  }
  const sites = [...bySite.values()];
  if (sites.length) {
    return (
      <div className="et-sites">
        {sites.map((l) => (
          <div key={l.site_id} className="et-site-line">
            {l.site_number && <span className="et-site-num">{l.site_number}</span>}
            <span>{l.site_name || ''}</span>
            {sites.length > 1 && <span className="et-site-amt">{fmtMoney(l.amount)}</span>}
          </div>
        ))}
      </div>
    );
  }
  if (r.site_id) {
    return (
      <div className="et-site-line">
        {r.site_number && <span className="et-site-num">{r.site_number}</span>}
        <span>{r.site_name || ''}</span>
      </div>
    );
  }
  return null;
}

export default function Etransfers() {
  const { user } = useAuth();
  // Filters live in the URL (?customer=12&method=eft) so a view such as
  // "Centex EFTs" can be bookmarked.
  const [sp, setSp] = useSearchParams();
  const urlSet = (k, v) => setSp((prev) => { const n = new URLSearchParams(prev); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const customer = sp.get('customer') || '';
  const method = sp.get('method') || '';
  const setMethod = (v) => urlSet('method', v);
  const setCustomer = (v) => urlSet('customer', v);
  const [status, setStatusRaw] = useState(sp.get('customer') ? 'all' : 'unmatched');
  const setStatus = setStatusRaw;
  const [days, setDays] = useState(sp.get('customer') ? '365' : '90');
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [note, setNote] = useState(null);
  const [scan, setScan] = useState(null);
  const [pdfText, setPdfText] = useState({});

  async function togglePdfText(row) {
    if (pdfText[row.id] != null) { setPdfText((t) => { const n = { ...t }; delete n[row.id]; return n; }); return; }
    const r = await axios.get(`/api/etransfers/${row.id}/text`).catch(() => ({ data: { text: '(could not load)' } }));
    setPdfText((t) => ({ ...t, [row.id]: r.data.text || '(no text saved for this one yet; run Scan past payments)' }));
  }

  const load = useCallback(() => {
    const params = { status };
    if (days !== 'all') params.days = days;
    if (method) params.method = method;
    if (customer) params.contact_id = customer;
    if (q.trim()) params.q = q.trim();
    return axios.get('/api/etransfers', { params })
      .then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, [status, days, q, method, customer]);

  useEffect(() => {
    const t = setTimeout(load, q ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, q]);

  // First visit with an empty queue: default to everything.
  useEffect(() => {
    if (data && status === 'unmatched' && data.unmatched === 0 && data.totals.count === 0 && !q) setStatus('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.unmatched]);

  useEffect(() => {
    axios.get('/api/etransfers/customers').then((r) => setCustomers(r.data.customers || [])).catch(() => {});
  }, [data?.totals?.count]);

  // Picking a customer: show their matched payments, not the Unmatched queue.
  useEffect(() => { if (customer && status === 'unmatched') setStatus('all'); }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (user?.role !== 'admin') return;
    axios.get('/api/etransfers/backfill-status').then((r) => setScan(r.data)).catch(() => {});
  }, [user?.role]);

  useEffect(() => {
    if (!scan?.running) return undefined;
    const t = setInterval(() => {
      axios.get('/api/etransfers/backfill-status').then((r) => {
        setScan(r.data);
        if (!r.data.running) load();
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(t);
  }, [scan?.running, load]);

  async function startScan() {
    await axios.post('/api/etransfers/backfill', { days: 365 });
    setScan((s) => ({ ...(s || {}), running: true }));
  }

  async function setIgnored(row, ignored) {
    const r = await axios.patch(`/api/etransfers/${row.id}`, { ignored, remember: ignored });
    if (ignored) {
      setNote(`Ignored everything from ${row.sender_name}${r.data.rematched ? ` (${r.data.rematched} more hidden)` : ''}. Find them under Ignored to restore.`);
      setTimeout(() => setNote(null), 6000);
    }
    load();
  }

  async function unassign(row) {
    await axios.patch(`/api/etransfers/${row.id}`, { contact_id: null });
    load();
  }

  const t = data?.totals;
  const tabs = [
    ['unmatched', `Unmatched${data ? ` (${data.unmatched})` : ''}`],
    ['matched', 'Matched'],
    ['all', 'All'],
    ['ignored', 'Ignored']
  ];

  return (
    <section className="page">
      <div className="page-header">
        <h2>Payments</h2>
        <div className="subtitle">Incoming Interac e-Transfers and EFT / cheque remittances, read from email and matched to customers through QuickBooks invoices.</div>
      </div>

      {data?.not_migrated && <p className="calls-error">Run server/scripts/migrate-etransfers.js to set this up.</p>}

      {t && (
        <div className="calls-tiles">
          <div className="calls-tile"><div className="calls-tile-value">{t.count}</div><div className="calls-tile-label">Payments</div></div>
          <div className="calls-tile calls-tile-good"><div className="calls-tile-value">{fmtMoney(t.total)}</div><div className="calls-tile-label">Total</div></div>
          <div className={`calls-tile${data.unmatched ? ' calls-tile-bad' : ''}`}><div className="calls-tile-value">{data.unmatched}</div><div className="calls-tile-label">Need a customer</div></div>
        </div>
      )}

      <div className="et-toolbar">
        <div className="em-chips">
          {tabs.map(([k, label]) => (
            <button key={k} type="button" className={`em-chip${status === k ? ' em-chip-on' : ''}`} onClick={() => setStatus(k)}>{label}</button>
          ))}
        </div>
        <div className="em-selects">
          <select value={customer} onChange={(e) => setCustomer(e.target.value)} className="et-customer-select">
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.contact_id} value={c.contact_id}>{c.company || `Customer #${c.contact_id}`} · {c.count}{c.eft_count ? ` (${c.eft_count} EFT)` : ''}</option>
            ))}
          </select>
          <select value={method} onChange={(e) => setMethod(e.target.value)}>
            <option value="">e-Transfers and EFTs</option>
            <option value="etransfer">e-Transfers only</option>
            <option value="eft">EFT / cheque only</option>
          </select>
          <select value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="365">Last 12 months</option>
            <option value="all">All time</option>
          </select>
          <input className="em-search" placeholder="Search sender, message, customer, amount" value={q} onChange={(e) => setQ(e.target.value)} />
          {user?.role === 'admin' && (
            <button type="button" className="btn btn-secondary btn-sm" onClick={startScan} disabled={scan?.running}
              title="Search Outlook for past e-Transfers and EFT remittances, then check their invoice numbers in QuickBooks">
              {scan?.running ? 'Scanning…' : 'Scan past payments'}
            </button>
          )}
        </div>
      </div>
      {user?.role === 'admin' && scan?.lastResult && !scan.running && (
        <p className="calls-dim">Last scan found {scan.lastResult.seen} e-Transfer emails and added {scan.lastResult.added} new{scan.lastResult.remittances != null ? `, plus ${scan.lastResult.remittances} new EFT remittances` : ''}{scan.lastResult.reprocessed && scan.lastResult.reprocessed.redone ? `. Re-read ${scan.lastResult.reprocessed.redone} EFTs, ${scan.lastResult.reprocessed.withInvoices} now with invoices` : ''}.
          {scan.lastResult.invoices && !scan.lastResult.invoices.error && ` Checked ${scan.lastResult.invoices.checked} against QuickBooks invoices (${scan.lastResult.invoices.matched} newly matched).`}
          {scan.lastResult.invoices?.error && <span className="calls-error"> QuickBooks: {scan.lastResult.invoices.error}</span>}
          {Object.entries(scan.lastResult.mailboxes || {}).filter(([, r]) => r.error).map(([mb, r]) => <span key={mb} className="calls-error"> {mb}: {r.error}</span>)}
        </p>
      )}
      {note && <p className="et-note">{note}</p>}
      {error && <p className="calls-error">{error}</p>}
      {!data && !error && <p className="calls-dim">Loading…</p>}

      {data && data.rows.length === 0 && (
        <p className="calls-dim">{status === 'unmatched' ? 'Nothing waiting. Every payment is matched.' : 'No payments here.'}</p>
      )}

      {data && data.rows.length > 0 && (
        <div className="et-table-wrap">
          <table className="et-table">
            <thead>
              <tr><th>Date</th><th className="et-num">Amount</th><th>From</th><th>Message</th><th>Customer / site</th><th /></tr>
            </thead>
            <tbody>
              {data.rows.map((r) => [
                <tr key={r.id} className={!r.contact_id && !r.ignored ? 'et-row-unmatched' : ''}>
                  <td className="et-date">{fmtDate(r.received_at)}</td>
                  <td className="et-num et-amount">{fmtMoney(r.amount)}<div><span className={`et-method et-method-${r.method || 'etransfer'}`}>{r.method === 'eft' ? 'EFT' : 'e-Transfer'}</span></div></td>
                  <td>{r.sender_name}{r.reference && <div className="calls-sub">{r.method === 'eft' ? r.reference : `Ref ${r.reference}`}</div>}</td>
                  <td className="et-msg">
                    {r.message || <span className="calls-dim">No message</span>}
                    <InvoiceInfo r={r} />
                    {r.method === 'eft' && user?.role === 'admin' && (
                      <button type="button" className="em-clear et-pdftext-btn" onClick={() => togglePdfText(r)}>
                        {pdfText[r.id] != null ? 'Hide PDF text' : 'PDF text'}
                      </button>
                    )}
                    {pdfText[r.id] != null && <pre className="et-pdftext">{pdfText[r.id]}</pre>}
                  </td>
                  <td>
                    {r.contact_id
                      ? <>
                          <Link to={`/customers/${r.contact_id}`}>{r.company || `Customer #${r.contact_id}`}</Link>
                          <SiteCell r={r} />
                          {r.match_method && <div className="calls-sub">{METHOD[r.match_method] || r.match_method}</div>}
                        </>
                      : r.ignored
                        ? <span className="calls-dim">{r.match_method === 'vendor' ? 'Vendor (auto-ignored)' : 'Ignored'}</span>
                        : <span className="calls-result-bad">Not matched</span>}
                  </td>
                  <td className="et-actions">
                    {r.ignored
                      ? <button type="button" className="btn btn-secondary btn-sm" onClick={() => setIgnored(r, false)}>Restore</button>
                      : <>
                          <button type="button" className={`btn btn-sm ${r.contact_id ? 'btn-secondary' : 'btn-primary'}`} onClick={() => setEditing(editing === r.id ? null : r.id)}>
                            {r.contact_id ? 'Change' : 'Assign'}
                          </button>
                          {r.contact_id && <button type="button" className="em-clear" onClick={() => unassign(r)} title="Remove the customer">Unassign</button>}
                          <button type="button" className="em-clear" onClick={() => setIgnored(r, true)} title="Not a customer payment. Hides every e-Transfer from this sender">Ignore</button>
                        </>}
                  </td>
                </tr>,
                editing === r.id && (
                  <tr key={`a-${r.id}`} className="et-assign-row">
                    <td colSpan={6}>
                      <EtransferAssign
                        row={r}
                        onCancel={() => setEditing(null)}
                        onDone={(res) => {
                          setEditing(null);
                          setNote(res.rematched ? `Saved. ${res.rematched} other e-Transfer${res.rematched === 1 ? '' : 's'} from the same sender matched too.` : 'Saved.');
                          setTimeout(() => setNote(null), 6000);
                          load();
                        }}
                      />
                    </td>
                  </tr>
                )
              ])}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
